/**
 * URL の search params を state のように扱う。
 * 前提: `window.location.search` 起点 = BrowserRouter / basename 無し。HashRouter や basename を入れるときは要変更。
 *
 * 一覧の条件・右パネル・詳細のタブは URL に載せる(戻るで閉じる・貼って共有できる)。
 * 空文字 / null は「消す」。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { SEARCH_DEBOUNCE_MS } from "../lib/useDebounced";

export type ParamPatch = Record<string, string | null | undefined>;

/**
 * 複数の param をまとめて書き換える。`replace` は履歴を増やさない。
 *
 * 起点は **いまの URL**(`window.location.search`)。react-router の関数形式
 * `setSearchParams(prev => …)` は描画時点の値を渡すので、同じ tick に 2 回
 * (検索語の debounce と条件チップなど)呼ぶと後の方が前の方を消していた。
 */
export function useSearchParamsPatch(): (patch: ParamPatch, replace?: boolean) => void {
  const [, setSp] = useSearchParams();
  return useCallback(
    (patch, replace = false) => {
      const next = new URLSearchParams(window.location.search);
      for (const [k, v] of Object.entries(patch)) {
        if (v == null || v === "") next.delete(k);
        else next.set(k, v);
      }
      setSp(next, { replace });
    },
    [setSp],
  );
}

/** 1 つの param を `[value, set]` で。 */
export function useSearchParam(
  name: string,
): [string, (value: string | null, replace?: boolean) => void] {
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const set = useCallback(
    (value: string | null, replace = false) => patch({ [name]: value }, replace),
    [name, patch],
  );
  return [sp.get(name) ?? "", set];
}

/**
 * 検索欄の入力を URL の param に載せる。`[input, setInput, urlValue]`。
 *
 * 入力は即座に state、落ち着いたら(debounce)URL へ(履歴は増やさない)。
 * **入力のたびに書込みを予約する**(値の変化を見るのではない)。「条件を外す」の
 * 直後に同じ語を打ち直すと、変化を見る方式では debounce の出力が変わらず URL に
 * 載らなかった(Codex レビュー 中-4)。
 *
 * 戻る・「条件を外す」など **外から** param が変わったときだけ入力欄を合わせ、
 * 予約は取り消す。自分で載せた値は react-router の startTransition で遅れて届く
 * ことがあり、その間に打った文字を「URL と違う」と消してしまっていた(e2e で再現)。
 * 載せた値を覚えておき、それが届いたときは入力欄を触らない。
 *
 * 返す setter には `cancel()` が付く(予約した書込みを取り消す)。FilterBar は IME の変換開始
 * (compositionstart)で取り消し、確定(compositionend)で確定値を予約し直す(fix1 低1。変換前の
 * 入力で予約した書込みが、変換中に URL と一覧を書き換えていた)。
 */
export type SearchInputSetter = ((value: string) => void) & { cancel: () => void };

export function useSearchParamInput(name: string): [string, SearchInputSetter, string] {
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const urlValue = sp.get(name) ?? "";
  const [input, setInputState] = useState(urlValue);
  // 自分が URL に載せて、まだ届いていない値(届いた順に消す)
  const pending = useRef<string[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 打った時点の URL の値。遅れて届いた urlValue がこれと同じなら、打つより前に
  // 起きていた変更(「条件を外す」の直後に打ち直した等)なので予約を捨てない
  const typedOn = useRef<string | null>(null);
  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const write = useCallback(
    (value: string) => {
      setInputState(value);
      cancel();
      const v = value.trim();
      typedOn.current = new URLSearchParams(window.location.search).get(name) ?? "";
      // 打った画面を覚える。debounce の間にリンクで別画面へ移ると、遷移が
      // startTransition で確定するまでこの画面がまだ残っていて、ここで書くと
      // 一覧(`?q=…`)へ引き戻してしまう(mdm e2e の iPhone で再現)
      const typedPath = window.location.pathname;
      timer.current = setTimeout(() => {
        timer.current = null;
        typedOn.current = null;
        if (window.location.pathname !== typedPath) return;
        const now = new URLSearchParams(window.location.search).get(name) ?? "";
        if (v === now) return;
        pending.current.push(v);
        patch({ [name]: v }, true);
      }, SEARCH_DEBOUNCE_MS);
    },
    [name, patch, cancel],
  );
  const setInput = useMemo(() => Object.assign(write, { cancel }), [write, cancel]);
  useEffect(() => {
    const i = pending.current.indexOf(urlValue);
    if (i >= 0) {
      pending.current.splice(0, i + 1);
      return;
    }
    // 打つ前に起きていた変更が、react-router の startTransition で遅れて届いただけ。
    // ここで入力欄を戻すと打ち直した語が消える(cypher の dev-e2e で再現)
    if (timer.current && urlValue === typedOn.current) return;
    // 外から変わった: 予約を捨てて入力欄を合わせる
    cancel();
    pending.current = [];
    setInputState(urlValue);
  }, [urlValue, cancel]);
  useEffect(() => cancel, [cancel]);
  return [input, setInput, urlValue];
}
